import { useCallback, useMemo } from 'react';
import { useQueryClient } from 'react-query';
import moment from 'moment';
import getRequest from '../../../requests/getRequest';
import updateRequestAddress from '../../../requests/updateRequestAddress';
import updateRequestService from '../../../requests/updateRequestService';
import updateRequestMaster from '../../../requests/updateRequestMaster';
import updateRequestExecuter from '../../../requests/updateRequestExecuter';
import updateRequestExecuteTime from '../../../requests/updateRequestExecuteTime';
import addCalendarEvent from '../../../requests/addCalendarEvent';
import updateCalendarEvent from '../../../requests/updateCalendarEvent';
import deleteCalendarEvent from '../../../requests/deleteCalendarEvent';
import useFilterState from './useFilterState';
import { filtersData } from '../constants/filtersData';
import { filterNames } from '../constants/filterNames';
import { getDashboardQueryKeys, getRequestsKeys } from '../queries/queryKeys';
import getDashboardRequest from '../../../requests/getDashboardRequest';

const useChangesMethodsForModals = () => {
  const queryClient = useQueryClient();
  const [[fromDate, toDate]] = useFilterState(
    filtersData[filterNames.createDate]
  );

  const requestsQueryKeys = useMemo(
    () => getRequestsKeys(fromDate, toDate),
    [fromDate, toDate]
  );

  const dashboardRequestsQueryKeys = useMemo(
    () => getDashboardQueryKeys(fromDate, toDate, false),
    [fromDate, toDate]
  );

  const getAndSetUpdatedRequest = useCallback(
    async (requestId) => {
      const id = typeof requestId === 'string' ? Number(requestId) : requestId;

      const updatedRequest = await getRequest(requestId);

      queryClient.setQueryData(requestsQueryKeys, (oldRequests) =>
        oldRequests?.map((request) => {
          if (request.id === id) {
            return updatedRequest[0];
          }

          return request;
        })
      );
    },
    [requestsQueryKeys, queryClient]
  );

  const getAndSetUpdatedDashboardRequest = useCallback(
    async (requestId) => {
      const id = typeof requestId === 'string' ? Number(requestId) : requestId;
      const updatedRequestsByStatus = await getDashboardRequest(id);
      if (
        !updatedRequestsByStatus ||
        typeof updatedRequestsByStatus !== 'object'
      ) {
        return;
      }
      let updated;
      let newStatusKey;
      for (const [statusKey, list] of Object.entries(updatedRequestsByStatus)) {
        const found = list.find((req) => req.id === id);
        if (found) {
          updated = found;
          newStatusKey = statusKey;
          break;
        }
      }
      if (!updated) {
        return;
      }
      queryClient.setQueryData(dashboardRequestsQueryKeys, (oldRequests) => {
        if (!oldRequests) return oldRequests;
        const newRequests = {};
        for (const [statusKey, requestsArray] of Object.entries(oldRequests)) {
          newRequests[statusKey] = requestsArray.filter((req) => req.id !== id);
        }
        if (!newRequests[newStatusKey]) {
          newRequests[newStatusKey] = [];
        }
        newRequests[newStatusKey].push(updated);
        return newRequests;
      });
    },
    [dashboardRequestsQueryKeys, queryClient]
  );

  const changeAddress = useCallback(
    async (values) => {
      const requestId = values.request;

      const data = {
        addressId: values.addressId,
        executorId: values.executerId,
        masterId: values.masterId,
        serviceId: values.typeId,
      };

      await updateRequestAddress(requestId, data);
      await getAndSetUpdatedDashboardRequest(requestId)
      await getAndSetUpdatedRequest(requestId);
    },
    [getAndSetUpdatedDashboardRequest, getAndSetUpdatedRequest]
  );

  const changeService = useCallback(
    async (values) => {
      const { newServiceId, request: requestId } = values;

      await updateRequestService(requestId, newServiceId);
      await getAndSetUpdatedDashboardRequest(requestId)
      await getAndSetUpdatedRequest(requestId);
    },
    [getAndSetUpdatedDashboardRequest, getAndSetUpdatedRequest]
  );

  const changeMaster = useCallback(
    async (values) => {
      const { request: requestId, masterId } = values;

      await updateRequestMaster(requestId, masterId);
      await getAndSetUpdatedDashboardRequest(requestId)
      await getAndSetUpdatedRequest(requestId);
    },
    [getAndSetUpdatedDashboardRequest, getAndSetUpdatedRequest]
  );

  const changeExecuter = useCallback(
    async (values) => {
      const { request: requestId, executerId } = values;

      await updateRequestExecuter(requestId, executerId);
      await getAndSetUpdatedDashboardRequest(requestId)
      await getAndSetUpdatedRequest(requestId);
    },
    [getAndSetUpdatedDashboardRequest, getAndSetUpdatedRequest]
  );

  const changeExecuteTime = useCallback(
    async (values) => {
      const { request: requestId, comment, executeTime, executeDate } = values;

      // const executeDateTime = moment(
      //   executeDate.format('YYYY-MM-DD') + executeTime.format('HH:mm')
      // );

      const executeDateTime = `${executeDate.format('YYYY-MM-DD')} ${executeTime.format('HH:mm')}`;

      // const executeDateTimeUtcStr = executeDateTime.clone().utc().format();

      const data = {
        executeDate: moment(executeDateTime).utc().format(),
        note: comment,
      };

      await updateRequestExecuteTime(requestId, data);
      await getAndSetUpdatedDashboardRequest(requestId)
      await getAndSetUpdatedRequest(requestId);
    },
    [getAndSetUpdatedDashboardRequest, getAndSetUpdatedRequest]
  );

  const postCalendarEvent = useCallback(
    async (values) => {
      const {
        eventId,
        executeDate,
        executeTimeEnd,
        executeTimeStart,
        executerId,
        request: requestId,
      } = values;

      if (executeTimeEnd.hour() === 0 && executeTimeEnd.minute() === 0) {
        executeTimeEnd.set({ hour: 23, minute: 59, second: 59 });
      }

      const fromDate = `${executeDate.format(
        'YYYY-MM-DD'
      )} ${executeTimeStart.format('HH:mm')}`;

      const toDate = `${executeDate.format(
        'YYYY-MM-DD'
      )} ${executeTimeEnd.format('HH:mm')}`;

      const calendarEvent = {
        requestId,
        workerId: executerId,
        fromDate: moment(fromDate).utc().format(),
        toDate: moment(toDate).utc().format(),
      };

      const eventFunc = async () =>
        eventId
          ? await updateCalendarEvent(eventId, calendarEvent)
          : await addCalendarEvent(calendarEvent);

      await eventFunc();
      await getAndSetUpdatedDashboardRequest(requestId)
      await getAndSetUpdatedRequest(requestId);
    },
    [getAndSetUpdatedDashboardRequest, getAndSetUpdatedRequest]
  );

  const removeCalendarEvent = useCallback(
    async (values) => {
      const { eventId, request: requestId } = values;

      await deleteCalendarEvent(eventId);
      await getAndSetUpdatedDashboardRequest(requestId)
      await getAndSetUpdatedRequest(requestId);
    },
    [getAndSetUpdatedDashboardRequest, getAndSetUpdatedRequest]
  );

  return {
    changeAddress,
    changeService,
    changeMaster,
    changeExecuter,
    changeExecuteTime,
    postCalendarEvent,
    removeCalendarEvent,
    getAndSetUpdatedRequest,
    getAndSetUpdatedDashboardRequest,
  };
};

export default useChangesMethodsForModals;
